<script lang="ts">

  import { goto } from "$app/navigation"
  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import { Calendar } from "$lib/components/ui/calendar"
  import { Checkbox } from "$lib/components/ui/checkbox"
  import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "$lib/components/ui/command"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Popover, PopoverButton, PopoverContent } from "$lib/components/ui/popover"
  import { RadioGroup, RadioGroupItem } from "$lib/components/ui/radio-group"
  import { Separator } from "$lib/components/ui/separator"
  import { SeparatorLabel } from "$lib/components/ui/separator-label"
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "$lib/components/ui/table"
  import { Textarea } from "$lib/components/ui/textarea"
  import type { _response } from "$lib/data/type/_response"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { getLocalTimeZone, parseDate, today } from "@internationalized/date"
  import CameraIcon from "@lucide/svelte/icons/camera"
  import PlusIcon from "@lucide/svelte/icons/plus"
  import XIcon from "@lucide/svelte/icons/x"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"
  import DialogFormAttendance from "./_components/dialog-form-attendance.svelte"
  import DialogFormDocumentation from "./_components/dialog-form-documentation.svelte"
  import DialogFormDocumentationAsset from "./_components/dialog-form-documentation-asset.svelte"
  import DialogFormDocumentationOther from "./_components/dialog-form-documentation-other.svelte"
  import { DocumentationAssetEmpty, DocumentationEmpty, RealizationFormSchema, type Activity, type Documentation, type DocumentationAsset, type Relation } from "../_types"

  const FormSchema = RealizationFormSchema
  const FormEmpty = FormSchema.parse({})

  const prefix = $derived(page.params.prefix ?? "0")
  const select = $derived(page.params.id ?? "")
  const problemDividerLabel = "Kendala"
  const nextDividerLabel = "Tahapan Selanjutnya"
  const tomorrow = today(getLocalTimeZone()).add({ days: 1 }).toString()
  const tomorrowDate = $derived(parseDate(tomorrow))

  const attendanceDialog = useDialog()
  const documentationDialog = useDialog()
  const assetDialog = useDialog()
  const otherDialog = useDialog()

  let documentationValue = $state<Documentation>(DocumentationEmpty)
  let assetValue = $state<DocumentationAsset>(DocumentationAssetEmpty)
  let otherValue = $state<Documentation>(DocumentationEmpty)
  let error = $state("")

  const form = createForm(() => ({
    defaultValues: FormEmpty,
    onSubmit: ({ value }) => mutation.mutate(value),
  }))
  const formWatch = form.useSelector((state) => state.values)
  const nextDate = $derived(formWatch.current.real_next_date)

  const statuses = createQuery(() => ({
    queryKey: ["master-status", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/realizations/master-status`)
      return response.data.result ?? []
    },
  }))

  const responses = createQuery(() => ({
    queryKey: ["master-response", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/realizations/master-response`)
      return response.data.result ?? []
    },
  }))

  const problems = createQuery(() => ({
    queryKey: ["master-problem", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/realizations/master-problem`)
      return response.data.result ?? []
    },
  }))

  const nexts = createQuery(() => ({
    queryKey: ["master-next", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/realizations/master-next`)
      return response.data.result ?? []
    },
  }))

  const query = createQuery(() => ({
    enabled: select !== "",
    queryKey: ["activity-realization-form", prefix, select],
    queryFn: async () => {

      const response = await httpClient.get<_response<Activity>>(`/api/${prefix}/activity/realizations/${select}`)
      const activity = response.data.result!

      form.reset({
        ...FormEmpty,
        id: activity.id,
        activity_id: activity.activity.id,
        activity_name: activity.activity.name,
        number: activity.number,
        date: activity.date,
        is_plan: activity.is_plan,
        category_name: activity.category.name,
        customer_type_name: activity.customer_type.name,
        customer_number: activity.customer_number,
        customer_name: activity.customer.name,
        is_other: activity.is_other ? 1 : 0,
        name: activity.name,
        description: activity.description,
        at_start: activity.at_start,
        at_end: activity.at_end,
        stage_name: activity.stage.name,
        status_name: activity.status.name,
        real_status_id: activity.real_status.id,
        real_status_name: activity.real_status.name,
        real_start: hasRealTime(activity.real_start_datetime),
        real_start_datetime: activity.real_start_datetime,
        real_end: hasRealTime(activity.real_end_datetime),
        real_end_datetime: activity.real_end_datetime,
        real_present: hasRealTime(activity.real_start_datetime),
        real_description: activity.real_description,
        real_response_id: activity.real_response.id,
        real_response_name: activity.real_response.name,
        real_problem_is: activity.real_problem_is ? 1 : 0,
        real_problem_id: activity.real_problem.id,
        real_problem_name: activity.real_problem.name,
        real_problem_description: activity.real_problem_description,
        real_next_is: activity.real_next_is ? 1 : 0,
        real_next_id: activity.real_next.id,
        real_next_name: activity.real_next.name,
        real_next_date: activity.real_next_date ? activity.real_next_date.toDate() : null,
        documentation_others: activity.documentation_others,
        documentation_assets: activity.documentation_assets,
        documentations: activity.documentations,
      })

      return activity
    },
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: z.infer<typeof FormSchema>) => {
      const body = new FormData()

      for (const [key, value] of Object.entries(data)) {
        if (!key.startsWith("documentation")) body.append(key, key === "real_next_date" ? value?.toString() ?? "" : String(value ?? ""))
      }
      body.set("_method", "PATCH")
      appendDocuments(body, "documentations", data.documentations)
      appendDocuments(body, "documentation_others", data.documentation_others)
      data.documentation_assets.forEach((document, index) => appendAsset(body, document, index))

      const response = await httpClient.postForm<_response<null>>(`/api/${prefix}/activity/realizations/${select}`, body)
      const responseData = response.data

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })
      if (response.status !== 200) return

      toast.success(responseData.message)
      await goto(`/${prefix}/activity-realization`, { replaceState: true })
    },
    onError: (cause: Error) => {
      error = cause.message
      toast.error("Terjadi kesalahan", { description: cause.message })
    },
  }))

  function appendDocuments(body: FormData, key: string, documents: Documentation[]) {
    documents.forEach((document, index) => {
      body.append(`${key}[${index}][id]`, String(document.id))
      body.append(`${key}[${index}][ref_id]`, document.ref_id)
      body.append(`${key}[${index}][name]`, document.name)
      body.append(`${key}[${index}][document_url]`, document.document_url)
      body.append(`${key}[${index}][document_delete]`, String(document.document_delete))
      if (document.document) body.append(`${key}[${index}][document]`, document.document)
    })
  }

  function appendAsset(body: FormData, document: DocumentationAsset, index: number) {
    const key = `documentation_assets[${index}]`
    body.append(`${key}[id]`, String(document.id))
    body.append(`${key}[ref_id]`, document.ref_id)
    body.append(`${key}[asset][id]`, String(document.asset.id))
    body.append(`${key}[asset][name]`, document.asset.name)
    body.append(`${key}[name]`, document.name)
    body.append(`${key}[description]`, document.description)
    body.append(`${key}[status]`, String(document.status))
    body.append(`${key}[document_url]`, document.document_url)
    body.append(`${key}[document_delete]`, String(document.document_delete))
    if (document.document) body.append(`${key}[document]`, document.document)
  }

  function hasRealTime(value: string) {
    return value.toDateTime().format("HH:mm:ss") !== "00:00:00"
  }

  function choose(field: string, value: Relation) {
    form.setFieldValue(`${field}_id` as never, value.id as never)
    form.setFieldValue(`${field}_name` as never, value.name as never)
    document.keyEsc()
  }

  function attendance(activity: Activity) {
    form.setFieldValue("real_present", hasRealTime(activity.real_start_datetime))
    form.setFieldValue("real_start", hasRealTime(activity.real_start_datetime))
    form.setFieldValue("real_start_datetime", activity.real_start_datetime)
    form.setFieldValue("real_end", hasRealTime(activity.real_end_datetime))
    form.setFieldValue("real_end_datetime", activity.real_end_datetime)
  }

  function saveDocumentation(value: Documentation) {
    const items = formWatch.current.documentations
    form.setFieldValue(
      "documentations",
      items.some((item) => item.ref_id === value.ref_id)
        ? items.map((item) => (item.ref_id === value.ref_id ? value : item))
        : [...items, value],
    )
    documentationDialog.setOpen(false)
  }

  function saveOther(value: Documentation) {
    const items = formWatch.current.documentation_others
    form.setFieldValue(
      "documentation_others",
      items.some((item) => item.ref_id === value.ref_id)
        ? items.map((item) => (item.ref_id === value.ref_id ? value : item))
        : [...items, value],
    )
    otherDialog.setOpen(false)
  }

  function saveAsset(value: DocumentationAsset) {
    const items = formWatch.current.documentation_assets
    form.setFieldValue(
      "documentation_assets",
      items.some((item) => item.ref_id === value.ref_id)
        ? items.map((item) => (item.ref_id === value.ref_id ? value : item))
        : [...items, value],
    )
    assetDialog.setOpen(false)
  }

  function removeDocumentation(value: Documentation) {
    form.setFieldValue(
      "documentations",
      formWatch.current.documentations.filter((item) => item.ref_id !== value.ref_id),
    )
    documentationDialog.setOpen(false)
  }

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/activity-realization`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full">
          <XIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Realisasi Form</h1>
      </div>
      <div></div>
    </div>
  </nav>
  <Loader show={query.isFetching || mutation.isPending}/>
  {#if query.isError}
    <div class="p-4 text-center text-destructive">Data aktivitas gagal dimuat</div>
  {:else}
    <div class="h-4"></div>
    <form
      action="#"
      autocomplete="off"
      class="px-3 space-y-3"
      onsubmit={(event) => {
        event.preventDefault()
        void form.handleSubmit()
      }}>
      <div class="flex">
        <div class="flex-1">
          <FieldLabel>Nomor</FieldLabel>
          <div class="font-mono">{formWatch.current.number || "-"}</div>
        </div>
        <div class="flex-1">
          <FieldLabel>Tanggal</FieldLabel>
          <div class="tabular-nums tracking-tight">{formWatch.current.date.toDateFormat("dd/MM/yyyy")}</div>
        </div>
      </div>
      <div>
        <FieldLabel>{formWatch.current.customer_type_name || "Tipe"}</FieldLabel>
        <div>{formWatch.current.customer_number} - {formWatch.current.customer_name}</div>
      </div>
      <div class="h-[1px]"></div>
      <SeparatorLabel label={formWatch.current.is_plan ? "Terencana" : "Tidak Terencana"}/>
      <div class="h-[1px]"></div>
      <div>
        <FieldLabel>Aktifitas Lain</FieldLabel>
        <div>{formWatch.current.is_other ? "Ya" : "Tidak"}</div>
      </div>
      <div>
        <FieldLabel>Kategori</FieldLabel>
        <div>{formWatch.current.category_name}</div>
      </div>
      <div>
        <FieldLabel>Aktifitas</FieldLabel>
        <div>{formWatch.current.name}</div>
      </div>
      <div>
        <FieldLabel>Keterangan</FieldLabel>
        <div>{formWatch.current.description}</div>
      </div>
      <div>
        <FieldLabel>Tanggal</FieldLabel>
        <div class="tabular-nums tracking-tight">{formWatch.current.date.toDateFormat("dd/MM/yyyy")}</div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <FieldLabel>Mulai</FieldLabel>
          <div class="tabular-nums tracking-tight">{formWatch.current.at_start.toDateFormat("HH:mm")}</div>
        </div>
        <div class="flex-1">
          <FieldLabel>Selesai</FieldLabel>
          <div class="tabular-nums tracking-tight">{formWatch.current.at_end.toDateFormat("HH:mm")}</div>
        </div>
      </div>
      <div class="h-[1px]"></div>
      <SeparatorLabel label="Realisasi"/>
      <div class="h-[1px]"></div>
      <div class="flex gap-3">
        <div class="flex-1 space-y-1 text-center">
          <FieldLabel>Mulai</FieldLabel>
          <Button
            type="button"
            size="sm"
            class="h-auto rounded-full px-3 py-1.5 shadow-none"
            onclick={() => attendanceDialog.setOpen(true)}>
            {formWatch.current.real_start_datetime.toDateFormat("HH:mm", "00:00")}
          </Button>
        </div>
        <div class="flex-1 space-y-1 text-center">
          <FieldLabel>Selesai</FieldLabel>
          <Button
            type="button"
            size="sm"
            class="h-auto rounded-full px-3 py-1.5 shadow-none"
            onclick={() => attendanceDialog.setOpen(true)}>
            {formWatch.current.real_end_datetime.toDateFormat("HH:mm", "00:00")}
          </Button>
        </div>
      </div>
      <div class="relative space-y-3">
        {#if !formWatch.current.real_present}
          <div class="absolute z-[1] flex h-full w-full items-center justify-center bg-background/80">
            Kehadiran belum dilakukan
          </div>
        {/if}
        <div>
          <FieldLabel>Status</FieldLabel>
          <Popover>
            <PopoverButton value={formWatch.current.real_status_name || "Pilih Status"}/>
            <PopoverContent>
              <Command>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each statuses.data ?? [] as item (item.id)}
                      <CommandItem
                        value={item.name}
                        onclick={() => choose("real_status", item)}>
                        {item.name}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FieldMessage
            name="real_status_id"
            form={form}/>
        </div>
        <form.Field name="real_description">
          {#snippet children(field)}
            <div>
              <FieldLabel>Catatan</FieldLabel>
              <Textarea
                value={field.state.value}
                oninput={(event) => field.handleChange(event.currentTarget.value)}/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <div>
          <FieldLabel>Respon User</FieldLabel>
          <Popover>
            <PopoverButton value={formWatch.current.real_response_name || "Pilih Respon"}/>
            <PopoverContent>
              <Command>
                <CommandList class="min-h-20">
                  <CommandEmpty>Tidak ada data</CommandEmpty>
                  <CommandGroup>
                    {#each responses.data ?? [] as item (item.id)}
                      <CommandItem
                        value={item.name}
                        onclick={() => choose("real_response", item)}>
                        {item.name}
                      </CommandItem>
                    {/each}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FieldMessage
            name="real_response_id"
            form={form}/>
        </div>
        <SeparatorLabel label={problemDividerLabel || formWatch.current.real_problem_name}/>
        <div>
          <FieldLabel>Kendala</FieldLabel>
          <RadioGroup
            name="real_problem_is"
            value={String(formWatch.current.real_problem_is)}
            class="flex gap-4">
            <label class="flex items-center gap-2 text-sm">
              <RadioGroupItem
                value="1"
                onclick={() => form.setFieldValue("real_problem_is", 1)}
              />
              <span>Ya</span>
            </label>
            <label class="flex items-center gap-2 text-sm">
              <RadioGroupItem
                value="0"
                onclick={() => form.setFieldValue("real_problem_is", 0)}
              />
              <span>Tidak</span>
            </label>
          </RadioGroup>
        </div>
        {#if formWatch.current.real_problem_is === 1}
          <div>
            <FieldLabel>Kategori Kendala</FieldLabel>
            <Popover>
              <PopoverButton value={formWatch.current.real_problem_name || "Pilih Kendala"}/>
              <PopoverContent>
                <Command>
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each problems.data ?? [] as item (item.id)}
                        <CommandItem
                          value={item.name}
                          onclick={() => choose("real_problem", item)}>
                          {item.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FieldMessage
              name="real_problem_id"
              form={form}/>
          </div>
          <form.Field name="real_problem_description">
            {#snippet children(field)}
              <div>
                <FieldLabel>Catatan Kendala</FieldLabel>
                <Textarea
                  value={field.state.value}
                  oninput={(event) => field.handleChange(event.currentTarget.value)}/>
                <FieldMessage
                  name={field.name}
                  form={form}/>
              </div>
            {/snippet}
          </form.Field>
        {/if}
        <SeparatorLabel label={nextDividerLabel || formWatch.current.real_next_name}/>
        <div>
          <FieldLabel>Tahapan Selanjutnya</FieldLabel>
          <RadioGroup
            name="real_next_is"
            value={String(formWatch.current.real_next_is)}
            class="flex gap-4">
            <label class="flex items-center gap-2 text-sm">
              <RadioGroupItem
                value="1"
                onclick={() => form.setFieldValue("real_next_is", 1)}
              />
              <span>Ya</span>
            </label>
            <label class="flex items-center gap-2 text-sm">
              <RadioGroupItem
                value="0"
                onclick={() => form.setFieldValue("real_next_is", 0)}
              />
              <span>Tidak</span>
            </label>
          </RadioGroup>
        </div>
        {#if formWatch.current.real_next_is === 1}
          <div>
            <FieldLabel>Tahapan Selanjutnya</FieldLabel>
            <Popover>
              <PopoverButton value={formWatch.current.real_next_name || "Pilih Tahapan"}/>
              <PopoverContent>
                <Command>
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each nexts.data ?? [] as item (item.id)}
                        <CommandItem
                          value={item.name}
                          onclick={() => choose("real_next", item)}>
                          {item.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FieldMessage
              name="real_next_id"
              form={form}/>
          </div>
          <div class="flex gap-3">
            <div class="flex-1">
              <FieldLabel>Tanggal</FieldLabel>
              <Popover>
                <PopoverButton
                  class="font-normal"
                  value={nextDate?.format("dd MMMM yyyy") || ""}/>
                <PopoverContent align="start">
                  <Calendar
                    type="single"
                    value={nextDate ?? undefined}
                    minValue={tomorrowDate}
                    onValueChange={(value) => {
                      if (!value) return
                      form.setFieldValue("real_next_date", value)
                      document.keyEsc()
                    }}/>
                </PopoverContent>
              </Popover>
            </div>
            <div class="flex-1"></div>
          </div>
        {/if}
        {#if formWatch.current.documentation_others.isNotEmpty()}
          <div class="h-[1px]"></div>
          <Table>
            <TableHeader class="select-none">
              <TableRow class="bg-background hover:bg-background">
                <TableHead class="min-w-[3rem] text-center">Foto</TableHead>
                <TableHead class="w-full text-start">Aktifitas Lain</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {#each formWatch.current.documentation_others as document (document.ref_id)}
                <TableRow class="border-b-0">
                  <TableCell class="text-center tabular-nums tracking-tight">
                    <Button
                      type="button"
                      variant={document.document || document.document_url ? "default" : "outline"}
                      size="icon-sm"
                      class="p-0 shadow-none"
                      onclick={() => {
                        otherValue = document
                        otherDialog.setOpen(true)
                      }}>
                      <CameraIcon aria-hidden="true"/>
                    </Button>
                  </TableCell>
                  <TableCell class="text-start text-sm">{document.name}</TableCell>
                </TableRow>
              {/each}
            </TableBody>
          </Table>
        {/if}
        {#if formWatch.current.documentation_assets.isNotEmpty()}
          <div class="h-[1px]"></div>
          <Table>
            <TableHeader class="select-none">
              <TableRow class="bg-background hover:bg-background">
                <TableHead class="min-w-[3rem] text-center">Foto</TableHead>
                <TableHead class="w-full text-start">Aset</TableHead>
                <TableHead class="min-w-[3rem] py-1.5 text-end">Kondisi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {#each formWatch.current.documentation_assets as asset (asset.ref_id)}
                <TableRow class="border-b-0">
                  <TableCell class="text-center tabular-nums tracking-tight">
                    <Button
                      type="button"
                      variant={asset.document || asset.document_url ? "default" : "outline"}
                      size="icon-sm"
                      class="p-0 shadow-none"
                      onclick={() => {
                        assetValue = asset
                        assetDialog.setOpen(true)
                      }}>
                      <CameraIcon aria-hidden="true"/>
                    </Button>
                  </TableCell>
                  <TableCell class="text-start text-xs">
                    <div>{asset.asset.name || asset.name}</div>
                    <div class="text-muted-foreground">{asset.description}</div>
                  </TableCell>
                  <TableCell class="flex justify-center">
                    <Checkbox
                      checked={asset.status}
                      class="flex-wrap shadow-none"
                      onclick={() => {
                        form.setFieldValue(
                          "documentation_assets",
                          formWatch.current.documentation_assets.map((item) =>  item.ref_id === asset.ref_id ? { ...item, status: !item.status } : item),
                        )
                      }}/>
                  </TableCell>
                </TableRow>
              {/each}
            </TableBody>
          </Table>
        {/if}
        <div class="h-[1px]"></div>
        <div class="flex items-center justify-between border-b pb-1">
          <h3>Dokumentasi</h3>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            class="shadow-none"
            onclick={() => {
              documentationValue = DocumentationEmpty
              documentationDialog.setOpen(true)
            }}>
            <PlusIcon aria-hidden="true"/>
          </Button>
        </div>
        <div class="space-y-2">
          {#if formWatch.current.documentations.isEmpty()}
            <div class="block rounded-md border px-3 py-2 text-center text-sm">Tidak ada data</div>
          {/if}
          {#each formWatch.current.documentations as document, index (document.ref_id)}
            <Button
              type="button"
              variant="outline"
              class="h-auto w-full justify-start"
              onclick={() => {
                documentationValue = document
                documentationDialog.setOpen(true)
              }}>
              Dokumentasi {index + 1}
            </Button>
          {/each}
        </div>
      </div>
      {#if error}
        <div
          class="text-xs text-destructive"
          role="alert">
          {error}
        </div>
      {/if}
      <Separator/>
      <div class="flex justify-between space-x-2">
        <div class="flex-1"></div>
        <Button
          type="submit"
          disabled={!formWatch.current.real_start || !formWatch.current.real_end}>
          Simpan
        </Button>
      </div>
      <div class="h-20"></div>
    </form>
  {/if}
</main>
<DialogFormAttendance
  open={attendanceDialog.state}
  setOpen={attendanceDialog.setOpen}
  select={select}
  callback={attendance}/>
<DialogFormDocumentation
  open={documentationDialog.state}
  setOpen={documentationDialog.setOpen}
  value={documentationValue}
  save={saveDocumentation}
  remove={removeDocumentation}/>
<DialogFormDocumentationAsset
  open={assetDialog.state}
  setOpen={assetDialog.setOpen}
  value={assetValue}
  save={saveAsset}/>
<DialogFormDocumentationOther
  open={otherDialog.state}
  setOpen={otherDialog.setOpen}
  value={otherValue}
  save={saveOther}
  remove={(value) =>
    form.setFieldValue(
      "documentation_others",
      formWatch.current.documentation_others.filter((item) => item.ref_id !== value.ref_id),
    )}
/>
